import Table from "../../components/Table/Table";
import { useContext, useState } from "react";
import PageTitle from "@/components/PageTitle";
import { ModalProvider, ModalTrigger } from "../../components/Modal/Modal";
import { defaultPersonColumns } from "../../config/person";
import Spinner from "../../components/spinner/Spinner";
import {
  DropdownContent,
  DropdownItem,
  DropdownItemIcon,
  DropdownRoot,
} from "../../components/dropdown/Dropdown";
import FiltersPerson from "../../components/filter/FiltersPerson";
import Pagination from "../../components/pagination/IndexPagination";
import { PagedPersonQueryContext } from "../../context/PagedPersonQueryProvider";
import Button from "@/components/bricks/button/Button";
import CloseSmallIcon from "../../components/icons/CloseSmallIcon";
import {
  PersonFiltersContext,
  PersonFiltersEventContext,
} from "../../context/FiltersContext";
import { QueryFilter, PersonFilterKeys } from "../../interfaces/filters";
import EmailIcon from "../../components/icons/EmailIcon";
import DownloadListIcon from "../../components/icons/DownloadListIcon";
import { QUERY_PAGE_SIZE } from "../../api/util";
import HandlingerDropdownTrigger from "../../components/Table/HandlingerDropdownTrigger";
import SendEmailModal from "../../components/sendEmail/SendEmailModal";
import { useReports } from "../../hooks/useQueries";
import ReportIcon from "../../components/icons/ReportIcon";
import { ReportLink } from "../../components/ReportLink";
import { ErrorBoundary } from "react-error-boundary";
import ErrorFallback from "../../components/ErrorFallback";
import { defaultSetSelected } from "../../context/PagedLinjeQueryProvider";
import { IRowData, SortBy } from "../../interfaces/table";
import useLocalStorageState from "use-local-storage-state";
import { PagedPersonsQueryField } from "../../interfaces/personer";
import { personIndexSortOrderKey } from "../../util/localStorageKeys";
import { downloadIndexTableToExcel } from "../../util/tableExportUtils";
import { Notification, useToaster } from "rsuite";
import DownloadPendingIcon from "../../components/icons/DownloadPendingIcon";
import WarningIcon from "../../components/icons/WarningIcon";
import { useNavigate } from "react-router-dom";
import ColumnEditorModal from "@/components/column-editor/ColumnEditorModal";
import { useSelectAllErrorToast } from "../../hooks/useSelectAllErrorToast";

const defaultSortBy: Array<SortBy<PagedPersonsQueryField>> = [
  {
    field: "fuldtNavn",
    direction: "asc",
  },
];

const Person = () => {
  return (
    <div className="l-main-row">
      <PageTitle title="Personregister" />
      <FiltersPerson />
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <Main />
      </ErrorBoundary>
    </div>
  );
};

const Main = () => {
  const {
    columns,
    error,
    loading,
    setActiveColumns,
    setQuery,
    pagedResponse,
    query,
    page,
    selection,
    emailableIds,
    selectAllError,
    clearSelectAllError,
  } = useContext(PagedPersonQueryContext);
  const {
    addSelectedIds,
    removeSelectedIds,
    selectAll,
    selectedIds,
    clearSelection,
    lastSelectedId,
    setLastSelectedId,
  } = selection;

  const navigate = useNavigate();
  const filters = useContext(PersonFiltersContext);
  const updateFilters = useContext(PersonFiltersEventContext);
  const { reports: allreports } = useReports();
  const reports = allreports?.filter(
    (r) => r.uiPath === "aika/web/personliste",
  );
  const [clear, setClear] = useState(false);
  const [activeModal, setActiveModal] = useState<ActiveModalTypes>("sendemail");
  const [sortBy, setSortBy] = useLocalStorageState<Array<SortBy>>(
    personIndexSortOrderKey,
    { defaultValue: defaultSortBy },
  );

  // for all your toasting purposes
  const toaster = useToaster();
  useSelectAllErrorToast(selectAllError, clearSelectAllError);

  if (error) {
    return (
      <div className="flex-grow-1 d-flex justify-content-center align-items-center text-muted text-center">
        Noget gik galt ...
        <br />
        {`Fejl: ${error.message}`}
      </div>
    );
  }

  if (loading) {
    return <Spinner className="flex-grow-1" label="Henter personer ..." />;
  }

  if (!pagedResponse) return null;
  const { personer } = pagedResponse;
  const { edges, pageInfo, totalCount } = personer;
  const { endCursor, startCursor, hasNextPage, hasPreviousPage } = pageInfo;

  const isAllSelected = totalCount === selectedIds.length;

  function downloadToExcel() {
    if (query) {
      toaster.push(
        <Notification>
          <div className="d-flex flex-row gap-3 align-items-center">
            <span className={`c-theme-icon c-theme-icon--person`}>
              <DownloadPendingIcon />
            </span>
            Forbereder fil download
          </div>
        </Notification>,
        {
          placement: "bottomEnd",
          duration: 2500,
        },
      );

      const promise = downloadIndexTableToExcel(
        selectedIds,
        query,
        "personer",
        columns,
        "export-personer",
      );

      promise.catch((error) => {
        toaster.push(
          <Notification>
            <div className="d-flex flex-row gap-3 align-items-center">
              <span className={`c-theme-icon bg-danger text-white`}>
                <WarningIcon />
              </span>
              <div className="d-flex flex-column gap-1">
                Der skete en fejl, da vi forberedte filen. Prøv igen eller
                kontakt deres administrator.
                <br />
                <small className="text-muted">
                  {error.status && `Fejl: ${error.status}`}
                </small>
              </div>
            </div>
          </Notification>,
          {
            placement: "bottomEnd",
            duration: 8000,
          },
        );
      });
    }
  }

  return (
    <main className="l-main__content">
      <header className="navbar navbar--sub">
        <div>
          <h2 className="me-2">
            {totalCount} person{totalCount === 1 ? "" : "er"}
          </h2>
          {/* <BadgeGroup>
          </BadgeGroup> */}
          {filters
            .filter(
              (f: QueryFilter<PersonFilterKeys>) => f.filterKey === "search",
            )
            .map((f: QueryFilter<PersonFilterKeys>) => {
              return (
                <span
                  className="badge badge--fixed-size bg-light text-dark rounded-pill"
                  key={f.filterKey}
                >
                  <span>{f.settings?.query}</span>
                  <Button
                    icon
                    size="sm"
                    color="secondary"
                    onClick={() => {
                      updateFilters({
                        event: "delete",
                        queryFilter: {
                          filterKey: f.filterKey,
                        },
                      });
                    }}
                  >
                    <CloseSmallIcon />
                  </Button>
                </span>
              );
            })}
        </div>
        <div className="justify-content-end gap-5">
          <ModalProvider>
            <DropdownRoot className="c-dropdown-default--actions">
              <HandlingerDropdownTrigger
                allSelected={isAllSelected}
                selectedIdsLength={selectedIds.length}
                totalCount={totalCount}
                type="personer"
                badgeSolid
              />
              <DropdownContent>
                <DropdownItem asChild>
                  <ModalTrigger
                    disabled={emailableIds?.length ? false : true}
                    onClick={() => {
                      setClear(true);
                      setActiveModal("sendemail");
                    }}
                  >
                    <DropdownItemIcon>
                      <EmailIcon />
                    </DropdownItemIcon>
                    <span className="d-flex gap-2 align-items-center">
                      Send e-mail...
                      {emailableIds?.length !== 0 && (
                        <div className="badge rounded-pill">
                          {emailableIds?.length}
                        </div>
                      )}
                    </span>
                  </ModalTrigger>
                </DropdownItem>
                <DropdownItem onClick={downloadToExcel}>
                  <DropdownItemIcon>
                    <DownloadListIcon />
                  </DropdownItemIcon>
                  <span>Hent til Excel</span>
                </DropdownItem>
                {reports &&
                  reports.map((r) => {
                    return (
                      <DropdownItem asChild key={r.id}>
                        <ReportLink
                          url={`${r.url}?ids=${selectedIds.join(",")}`}
                          className="c-dropdown-default-item "
                        >
                          <DropdownItemIcon>
                            <ReportIcon />
                          </DropdownItemIcon>
                          <span>Download {r.title}</span>
                        </ReportLink>
                      </DropdownItem>
                    );
                  })}
              </DropdownContent>
            </DropdownRoot>
            <Pagination
              hasNextPage={hasNextPage}
              hasPreviousPage={hasPreviousPage}
              page={page}
              totalCount={totalCount}
              onPreviousClick={() => {
                setQuery({
                  ...query,
                  page: page - 1,
                  after: undefined,
                  before: startCursor,
                });
              }}
              onNextClick={() => {
                setQuery({
                  ...query,
                  page: page + 1,
                  before: undefined,
                  after: endCursor,
                });
              }}
            />
            {activeModal === "sendemail" && (
              <SendEmailModal
                type="person"
                contextData={{
                  selectedIds: emailableIds,
                  totalCount: emailableIds.length,
                  allSelected: isAllSelected,
                }}
                clear={clear}
                setClear={setClear}
              />
            )}
          </ModalProvider>
        </div>
      </header>
      <div className="l-main__table px-2">
        <ModalProvider>
          <Table
            onRowClick={(data: IRowData) => {
              navigate(`/person/${data.id}`);
            }}
            className="table--selectable"
            columns={columns}
            keyField="id"
            data={edges.map((e) => {
              return {
                ...e.node,
                selected: selectedIds.indexOf(e.node.id) > -1,
              };
            })}
            selectRow={{
              setSelected: (rowData, selected, shiftKeyPressed) => {
                defaultSetSelected(
                  rowData,
                  selected,
                  shiftKeyPressed,
                  edges,
                  lastSelectedId,
                  addSelectedIds,
                  removeSelectedIds,
                  setLastSelectedId,
                );
              },
              toggleSelectAllOnPage: (mode) => {
                if (mode === "all") {
                  addSelectedIds(edges.map((e) => e.node.id));
                } else {
                  removeSelectedIds(edges.map((e) => e.node.id));
                }
              },
              isAllSelected: isAllSelected,
              selectAll,
              clearSelection,
            }}
            pageInfo={{
              pageSize: QUERY_PAGE_SIZE,
              totalNumberOfRows: totalCount,
            }}
            sortings={{
              defaultSortBy: defaultSortBy,
              sortBy: sortBy,
              setSortBy: setSortBy,
            }}
          />
          <ColumnEditorModal
            columns={columns}
            setActiveColumns={setActiveColumns}
            defaultColumns={defaultPersonColumns}
          />
        </ModalProvider>
      </div>
    </main>
  );
};

export default Person;
